import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import Button from '../../components/common/Button';
import SessionJoinAction from '../../components/classroom/SessionJoinAction';
import SessionHostAction from '../../components/classroom/SessionHostAction';
import { formatDateTime } from '../../utils/date';
import { getClassroomLive } from '../../api/classroom';
import { getApiErrorInfo } from '../../utils/apiError';
import { useParentChild } from '../../context/ParentChildContext';
import { localizeBatchName } from '../../utils/displayNames';

/**
 * Live / upcoming classroom list with Start (host) / Join actions.
 */
export default function ClassroomLive({ role = 'ADMIN' }) {
  const { t } = useTranslation();
  const {
    selectedStudentId,
    loading: childLoading,
    approvedChildren,
    loadError: childLoadError,
    refresh: refreshChildren,
  } = useParentChild();
  const base = `/${String(role).toLowerCase()}/classroom`;
  const isStaff = role === 'ADMIN' || role === 'MANAGER' || role === 'COACH';
  const isAdmin = role === 'ADMIN';
  const isParent = role === 'PARENT';
  const sessionsPath =
    role === 'ADMIN' ? '/admin/sessions'
      : role === 'MANAGER' ? '/manager/sessions'
        : role === 'COACH' ? '/coach/sessions'
          : role === 'PARENT' ? '/parent/sessions'
            : '/student/sessions';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [pollWarning, setPollWarning] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (isParent && childLoading) {
      return;
    }
    if (isParent && !selectedStudentId) {
      setData({ zoomConnected: false, sessions: [] });
      setError('');
      setPollWarning('');
      setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
    }
    if (!silent) {
      setError('');
    }
    try {
      const live = await getClassroomLive(isParent ? selectedStudentId : undefined);
      setData(live);
      setPollWarning('');
    } catch (err) {
      const message = getApiErrorInfo(err, t('classroom.live.statusLoadFailed')).message;
      if (silent) {
        setPollWarning(message);
      } else {
        setData(null);
        setError(message);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [isParent, childLoading, selectedStudentId, t]);

  useEffect(() => {
    if (isParent && childLoading) {
      setLoading(true);
      return;
    }
    load();
  }, [load, isParent, childLoading]);

  useEffect(() => {
    if (needsChildForPoll(isParent, selectedStudentId, childLoading)) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      load({ silent: true });
    }, 20000);
    return () => window.clearInterval(timer);
  }, [load, isParent, selectedStudentId, childLoading]);

  const meetingStatusLabel = (status) => {
    const raw = String(status || '').toUpperCase();
    if (raw === 'CREATING') return t('classroom.live.meetingStatusCreating');
    if (raw === 'STARTED') return t('classroom.live.meetingStatusStarted');
    if (raw === 'FAILED') return t('classroom.live.meetingStatusFailed');
    if (raw === 'CREATED') return t('classroom.live.meetingReady');
    if (raw === 'ENDED') return t('classroom.live.meetingStatusEnded');
    if (!raw) return '';
    return t('classroom.live.meetingStatusUnknown');
  };

  if (loading || (isParent && childLoading)) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('classroom.live.title')}
        subtitle={
          isStaff
            ? (isAdmin ? t('classroom.live.subtitleAdmin') : t('classroom.live.subtitleStaff'))
            : t('classroom.live.subtitleLearner')
        }
        backTo={base}
      />
    );
  }

  const zoomConnected = !!data?.zoomConnected;
  const externalMode = String(data?.defaultProvider || '').toUpperCase() === 'EXTERNAL_LINK';
  const classroomReady = zoomConnected || externalMode;
  const sessions = data?.sessions || [];
  const needsChild = isParent && !selectedStudentId && !childLoadError;
  const noLinkedChildren = isParent && !childLoading && !childLoadError && Array.isArray(approvedChildren) && approvedChildren.length === 0;
  const liveSubtitle = isStaff
    ? (isAdmin ? t('classroom.live.subtitleAdmin') : t('classroom.live.subtitleStaff'))
    : t('classroom.live.subtitleLearner');

  return (
    <div className="page-shell-form max-w-4xl">
      <PageHeaderSection
        eyebrow={t('nav.classroom')}
        title={t('classroom.live.title')}
        subtitle={liveSubtitle}
        showBack
        backTo={base}
      />

      {needsChild && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
          {noLinkedChildren
            ? t('classroom.live.noChildrenHint')
            : t('classroom.live.selectChildHint')}
        </div>
      )}

      {childLoadError && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          <p>{t('classroom.live.childrenLoadFailed')}</p>
          <Button type="button" variant="page-secondary" className="mt-2" onClick={() => refreshChildren()}>
            {t('classroom.live.refresh')}
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          <p>{error}</p>
          <Button type="button" variant="page-secondary" className="mt-2" onClick={() => load()}>
            {t('classroom.live.refresh')}
          </Button>
        </div>
      )}
      {pollWarning && !error && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
          <p>{pollWarning}</p>
          <Button type="button" variant="page-secondary" className="mt-2" onClick={() => load()}>
            {t('classroom.live.refresh')}
          </Button>
        </div>
      )}

      {!needsChild && !error && !childLoadError && (
        <div
          className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
            classroomReady
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-950'
          }`}
          role="status"
        >
          {classroomReady ? (
            externalMode && !zoomConnected
              ? t('classroom.live.externalModeHint')
              : t('classroom.live.zoomConnectedHint')
          ) : (
            <>
              {isStaff ? t('classroom.live.zoomPendingHint') : t('classroom.live.zoomPendingHintLearner')}
              {isAdmin && (
                <>
                  {' '}
                  <Link to={`${base}/zoom`} className="font-semibold text-amber-900 underline hover:no-underline">
                    {t('classroom.live.connectZoomCta')}
                  </Link>
                </>
              )}
            </>
          )}
        </div>
      )}

      {!error && !childLoadError && (
      <div className="list-table-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t('classroom.live.todayHeading')}</h2>
            <p className="text-sm text-slate-500">
              {isStaff ? t('classroom.live.todayHintLive') : t('classroom.live.todayHintLearner')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="page-secondary" type="button" onClick={() => load()} disabled={needsChild}>
              {t('classroom.live.refresh')}
            </Button>
            {isStaff && (
              <Link
                to={sessionsPath}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {t('classroom.live.openSessions')}
              </Link>
            )}
          </div>
        </div>

        {needsChild ? (
          <div className="px-5 py-12 text-center">
            <p className="font-medium text-slate-800">
              {noLinkedChildren
                ? t('classroom.live.noChildrenTitle')
                : t('classroom.live.selectChildTitle')}
            </p>
            <p className="mt-2 mx-auto max-w-md text-sm text-slate-500">
              {noLinkedChildren
                ? t('classroom.live.noChildrenHint')
                : t('classroom.live.selectChildHint')}
            </p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="font-medium text-slate-800">{t('classroom.live.emptyTitle')}</p>
            <p className="mt-2 mx-auto max-w-md text-sm text-slate-500">
              {classroomReady
                ? (isStaff ? t('classroom.live.emptyStaffConnected') : t('classroom.live.emptyLearnerConnected'))
                : (isStaff ? t('classroom.live.emptyStaff') : t('classroom.live.emptyLearner'))}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sessions.map((s) => (
              <li key={s.sessionId} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900">{localizeBatchName(s.batchName, t) || t('classroom.live.unnamedSession')}</p>
                  <p className="text-sm text-slate-500">
                    {formatDateTime(s.startTime)}
                    {s.coachFullName ? ` · ${s.coachFullName}` : ''}
                  </p>
                  <p className="mt-1 text-xs font-medium text-slate-500">
                    {s.hasMeeting
                      ? (String(s.meetingStatus || '').toUpperCase() === 'CREATED'
                        ? t('classroom.live.meetingReady')
                        : (s.meetingStatus ? meetingStatusLabel(s.meetingStatus) : t('classroom.live.meetingReady')))
                      : t('classroom.live.meetingNotCreated')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 items-start">
                  {s.canStart && (
                    <SessionHostAction
                      sessionId={s.sessionId}
                      role={role}
                      classroomEligible
                      hasMeeting={Boolean(s.hasMeeting)}
                      meetingStatus={s.meetingStatus}
                      status={s.status}
                      endTime={s.endTime}
                      onStarted={() => load({ silent: true })}
                      onEnded={() => load({ silent: true })}
                    />
                  )}
                  {!(isStaff && s.canStart) && (
                    <SessionJoinAction
                      sessionId={s.sessionId}
                      role={role}
                      classroomEligible
                      canJoin={Boolean(s.canJoin)}
                      meetingStatus={s.meetingStatus}
                      status={s.status}
                      endTime={s.endTime}
                      studentId={isParent ? selectedStudentId : undefined}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      )}
    </div>
  );
}

function needsChildForPoll(isParent, selectedStudentId, childLoading) {
  return isParent && (childLoading || !selectedStudentId);
}
